import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { PAIRS, PAIR_GROUPS, STRATEGIES } from '../utils/constants.js';
import { calcStats, fmt$, exportCSV } from '../utils/helpers.js';
import { usePieChart, useEquityCurve } from '../hooks/useCharts.jsx';
import { toastSuccess, toastInfo } from '../utils/toast.js';
import '../styles/dashboard-zella.css';

// ── Pip calculator ─────────────────────────────────────────────────────────
// Always returns a finite number — never a string, null, undefined, or NaN —
// so every call site can safely call .toFixed() on the result without guards.
function calcPips(t) {
  const toNum = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const entry = parseFloat(t.entry);
  const tp    = parseFloat(t.tp);
  const sl    = parseFloat(t.sl);

  if (!entry || (!tp && !sl)) return toNum(t.pips);

  const isWin  = Number(t.result) > 0;
  const exit   = isWin ? tp : sl;
  if (!exit) return toNum(t.pips);

  const pair   = (t.pair || '').toUpperCase();
  let pipSize;
  if (pair.includes('XAU') || pair.includes('GOLD'))                          pipSize = 0.1;
  else if (pair.includes('XAG') || pair.includes('SILVER'))                   pipSize = 0.01;
  else if (pair.endsWith('JPY') || pair.includes('JPY'))                      pipSize = 0.01;
  else if (/NASDAQ|US30|US500|GER30|UK100|JP225|AUS200|FRA40/.test(pair))     pipSize = 1;
  else if (/BTC|ETH|BNB|SOL|XRP/.test(pair))                                  pipSize = 1;
  else                                                                        pipSize = 0.0001;

  const raw = t.dir === 'BUY' ? (exit - entry) : (entry - exit);
  const result = parseFloat((raw / pipSize).toFixed(1));
  return Number.isFinite(result) ? result : 0;
}

// ── R:R calculator ──────────────────────────────────────────────────────────
function calcRR(t) {
  const entry = parseFloat(t.entry);
  const sl    = parseFloat(t.sl);
  const tp    = parseFloat(t.tp);
  if (!entry || !sl || !tp) return t.rr != null ? parseFloat(t.rr) || null : null;
  const risk   = Math.abs(entry - sl);
  const reward = Math.abs(tp - entry);
  if (risk === 0) return t.rr != null ? parseFloat(t.rr) || null : null;
  return parseFloat((reward / risk).toFixed(2));
}

/* ── SKELETON TABLE ROW ── */
function SkeletonTableRow() {
  return (
    <tr className="tbl-skeleton-row">
      <td className="tbl-td-check"><div className="skeleton" style={{ width: 12, height: 12 }}/></td>
      <td style={{ padding: '10px 0', width: 3 }}><div style={{ width: 2, height: 28, background: 'var(--dark4)' }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-text" style={{ width: 48, marginBottom: 4 }}/><div className="skeleton skeleton-text" style={{ width: 28 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-text" style={{ width: 36 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-text" style={{ width: 56, marginBottom: 4 }}/><div className="skeleton skeleton-text" style={{ width: 32 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-chip" style={{ width: 38, height: 18 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-text" style={{ width: 44 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-text" style={{ width: 44 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-text" style={{ width: 44 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-text" style={{ width: 52, marginBottom: 4 }}/><div className="skeleton skeleton-text" style={{ width: 24 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-text" style={{ width: 36 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-text" style={{ width: 30 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton skeleton-chip" style={{ width: 72, height: 20 }}/></td>
      <td className="tbl-td-pad"><div className="skeleton" style={{ width: 30, height: 20 }}/></td>
      <td className="tbl-td-pad"><div className="tbl-actions">{[0,1,2].map(i => <div key={i} className="skeleton" style={{ width: 26, height: 26 }}/>)}</div></td>
    </tr>
  );
}

/* ── STRATEGY BREAKDOWN ── */
function StrategyBreakdown({ filtered }) {
  const sm = {};
  filtered.forEach(t => {
    if (!sm[t.strat]) sm[t.strat] = { n: 0, pnl: 0, wins: 0 };
    sm[t.strat].n++;
    sm[t.strat].pnl += t.result;
    if (t.result > 0) sm[t.strat].wins++;
  });
  const entries = Object.entries(sm).sort((a, b) => b[1].pnl - a[1].pnl);
  if (!entries.length) return <div className="txt-caption strat-empty">No data</div>;
  return (
    <>
      {entries.map(([strat, d]) => {
        const wr = d.n ? Math.round(d.wins / d.n * 100) : 0;
        return (
          <div key={strat} className="strat-row">
            <div className="strat-row-hd">
              <span className="txt-caption txt-truncate strat-name">{strat}</span>
              <div className="strat-stats">
                <span className={`txt-caption ${wr >= 50 ? 'strat-wr-pos' : 'strat-wr-neg'}`}>{wr}%</span>
                <span className={`txt-caption fw-semibold ${d.pnl >= 0 ? 'strat-pnl-pos' : 'strat-pnl-neg'}`}>{fmt$(d.pnl)}</span>
              </div>
            </div>
            <div className="strat-bar-track">
              <div className={`strat-bar-fill ${d.pnl >= 0 ? 'pos' : 'neg'}`} style={{ width: `${Math.min(100, d.n / Math.max(filtered.length, 1) * 100)}%` }}/>
            </div>
          </div>
        );
      })}
    </>
  );
}

/* ── SVG STATUS ICONS ── */
const IcoCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" aria-hidden="true">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IcoCross = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2.5" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ── ICONS ── */
const IcoView = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const IcoEdit = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IcoDel  = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;

/* ── CONFIRM MODAL ── */
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="cm-overlay">
      <div className="cm-box" onClick={e => e.stopPropagation()}>
        <div className="cm-gold-bar"/>
        <div className="cm-header">
          <div className="cm-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/>
              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
            </svg>
          </div>
          <div className="cm-title">Confirm Delete</div>
        </div>
        <div className="cm-body">
          <div className="cm-message">{message}</div>
          <div className="cm-actions">
            <button onClick={onCancel}  className="cm-btn cm-btn-cancel">Cancel</button>
            <button onClick={onConfirm} className="cm-btn cm-btn-delete">Delete</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── TRADE IMAGE VIEWER ── */
function TradeImageViewer({ imgBefore, imgAfter }) {
  const [active, setActive]     = useState('before');
  const [expanded, setExpanded] = useState(false);

  const src     = active === 'before' ? imgBefore : imgAfter;
  const hasBoth = imgBefore && imgAfter;

  const download = () => {
    if (!src) return;
    const a = document.createElement('a');
    a.href = src;
    a.download = `chart-${active}-${Date.now()}.jpg`;
    a.click();
  };

  return (
    <>
      <div className="tiv-wrap">
        {hasBoth && (
          <div className="tiv-tabs">
            {[['before','📷 Before'],['after','📸 After']].map(([key,label])=>(
              <button key={key} onClick={() => setActive(key)}
                className={`tiv-tab ${active === key ? 'active' : 'inactive'}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="tiv-toolbar">
          <span className="tiv-label">
            {hasBoth ? (active==='before' ? 'Before Trade' : 'After Trade') : (imgBefore ? 'Chart Screenshot' : 'After Trade')}
          </span>
          <div className="tiv-tools">
            <button onClick={() => setExpanded(true)} title="Expand" className="tiv-tool-btn">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
              Expand
            </button>
            <button onClick={download} title="Download" className="tiv-tool-btn">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Save
            </button>
          </div>
        </div>

        <div className="tiv-img-wrap" onClick={() => setExpanded(true)}>
          <img src={src} alt={active} className="tiv-img"/>
        </div>
      </div>

      {expanded && (
        <div className="tiv-lightbox" onClick={() => setExpanded(false)}>
          <div className="tiv-lb-toolbar" onClick={e => e.stopPropagation()}>
            {hasBoth ? (
              <div className="flex-gap6">
                {[['before','Before'],['after','After']].map(([key,label])=>(
                  <button key={key} onClick={() => setActive(key)}
                    className={`tiv-lb-tab ${active === key ? 'active' : 'inactive'}`}>
                    {label}
                  </button>
                ))}
              </div>
            ) : <div/>}
            <div className="tiv-lb-actions">
              <button onClick={download} className="tiv-lb-btn">↓ Save</button>
              <button onClick={() => setExpanded(false)} className="tiv-lb-close">✕ Close</button>
            </div>
          </div>
          <div className="tiv-lb-img-wrap" onClick={e => e.stopPropagation()}>
            <img src={src} alt={active} className="tiv-lb-img"/>
          </div>
        </div>
      )}
    </>
  );
}

const LOAD_MORE_STEP = 20;

export default function JournalPage({ trades = [], onDelete, onEdit, onNewTrade, isLoading = false }) {
  const [search,       setSearch]       = useState('');
  const [filterPair,   setFilterPair]   = useState('');
  const [filterDir,    setFilterDir]    = useState('');
  const [filterRes,    setFilterRes]    = useState('');
  const [filterStrat,  setFilterStrat]  = useState('');
  const [sortKey,      setSortKey]      = useState('date');
  const [sortDir,      setSortDir]      = useState(-1);
  const [selected,     setSelected]     = useState(new Set());
  const [confirm,      setConfirm]      = useState(null);
  const [viewTrade,    setViewTrade]    = useState(null);
  const [visibleCount, setVisibleCount] = useState(LOAD_MORE_STEP);
  const [filtersOpen,  setFiltersOpen]  = useState(false);
  const [stratOpen,    setStratOpen]    = useState(false);

  const pieRef = useRef(null);
  const eqRef  = useRef(null);

  const filtered = useMemo(() => trades.filter(t => {
    if (filterPair  && t.pair  !== filterPair)  return false;
    if (filterDir   && t.dir   !== filterDir)   return false;
    if (filterRes === 'win'  && t.result <= 0)  return false;
    if (filterRes === 'loss' && t.result >= 0)  return false;
    if (filterStrat && t.strat !== filterStrat) return false;
    if (search) {
      const q   = search.toLowerCase();
      const hay = [t.pair, t.dir, t.strat, t.session, t.emotion, t.notes, t.review, t.date].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (sortKey === 'result') { av = Number(a.result); bv = Number(b.result); }
    if (av < bv) return -sortDir;
    if (av > bv) return sortDir;
    return 0;
  }), [trades, filterPair, filterDir, filterRes, filterStrat, search, sortKey, sortDir]);

  const filterKey = `${filterPair}|${filterDir}|${filterRes}|${filterStrat}|${search}|${sortKey}|${sortDir}`;
  const prevFilterKey = useRef('');
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      setVisibleCount(20);
    }
  }, [filterKey]);

  const paginated = filtered.slice(0, visibleCount);
  const hasMore   = filtered.length > visibleCount;

  usePieChart(pieRef, filtered);
  useEquityCurve(eqRef, filtered);
  const s = calcStats(filtered);

  const stratRef = useRef(null);
  useEffect(() => {
    if (!stratOpen) return;
    const handler = e => { if (stratRef.current && !stratRef.current.contains(e.target)) setStratOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [stratOpen]);

  const srt = key => { if (sortKey === key) setSortDir(d => -d); else { setSortKey(key); setSortDir(-1); } };
  const Arrow = ({ k }) => sortKey === k
    ? <span className="txt-caption txt-accent sort-arrow">{sortDir===-1?'▼':'▲'}</span>
    : null;

  const allChecked = filtered.length > 0 && filtered.every(t => selected.has(t.id));
  const toggleAll  = () => setSelected(allChecked ? new Set() : new Set(filtered.map(t => t.id)));
  const toggleOne  = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const deleteSelected = () => {
    if (!selected.size) return;
    const count = selected.size;
    setConfirm({
      message: `Delete ${count} trade${count > 1 ? 's' : ''}? This cannot be undone.`,
      onConfirm: () => { selected.forEach(id => onDelete(id)); setSelected(new Set()); toastInfo(`${count} trade${count > 1 ? 's' : ''} deleted`); }
    });
  };

  const deleteSingle = (id) => {
    setConfirm({ message: 'Delete this trade? This cannot be undone.', onConfirm: () => { onDelete(id); toastInfo('Trade deleted'); }});
  };

  const [exportFlash, setExportFlash] = useState(false);
  const handleExportCSV = () => {
    exportCSV(filtered);
    setExportFlash(true);
    setTimeout(() => setExportFlash(false), 2000);
    toastSuccess('CSV exported successfully');
  };

  const clearFilters = () => { setFilterPair(''); setFilterDir(''); setFilterRes(''); setFilterStrat(''); setSearch(''); };

  const kpiSparkPts = useMemo(() => {
    const sorted = [...filtered].sort((a,b) => (a.date||'').localeCompare(b.date||''));
    let run = 0;
    return sorted.map(t => { run += (t.result||0); return run; });
  }, [filtered]);

  const hasActive = filterPair||filterDir||filterRes||filterStrat;

  return (
    <div className="journal-page">
      {confirm && <ConfirmModal message={confirm.message} onConfirm={()=>{ try { confirm.onConfirm(); } finally { setConfirm(null); } }} onCancel={()=>setConfirm(null)}/>}

      {/* ── STICKY TOP ── */}
      <div className="journal-sticky-top">
        <div className="journal-header">
          <div>
            <div className="txt-micro journal-breadcrumb">KFX Portal / Trading</div>
            <div className="h2">Trade <span className="journal-title-gold">Journal</span></div>
            <div className="txt-caption txt-secondary journal-subtitle">Every trade logged. Every lesson captured.</div>
          </div>
          <div className="header-btns">
            {selected.size > 0 && (
              <button className="btn-danger flex-gap5" onClick={deleteSelected}>
                <IcoDel/> Delete {selected.size}
              </button>
            )}
            <button className="btn-outline flex-gap5" onClick={handleExportCSV}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {exportFlash ? '✓ Exported' : 'Export CSV'}
            </button>
            <button className="btn-gold" onClick={onNewTrade}>+ Log Trade</button>
          </div>
        </div>

        <div className="zl-kpi-strip journal-kpi-strip">
          <div className={`zl-kpi zl-kpi--${s.net >= 0 ? 'green' : 'red'}`}>
            <div className="zl-kpi-label">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              Net cumulative P&amp;L
              <span className="zl-kpi-label-suffix" style={{fontSize:'9px',color:'var(--gray)',marginLeft:'auto'}}>{s.total} trades</span>
            </div>
            <div className="zl-kpi-body">
              <div className={`zl-kpi-num zl-kpi-num--${s.net >= 0 ? 'green' : 'red'}`}>{fmt$(s.net)}</div>
              {kpiSparkPts.length >= 2 && (() => {
                const mn = Math.min(...kpiSparkPts), mx = Math.max(...kpiSparkPts), range = mx - mn || 1;
                const W = 80, H = 36;
                const color = s.net >= 0 ? 'var(--green)' : 'var(--red)';
                const pathD = kpiSparkPts.map((v,i) => `${i===0?'M':'L'}${(i/(kpiSparkPts.length-1))*W},${H-((v-mn)/range)*(H-4)+2}`).join(' ');
                return (
                  <svg viewBox={`0 0 ${W} ${H}`} className="zl-kpi-spark" style={{width:80,height:36,flexShrink:0}} preserveAspectRatio="none">
                    <defs><linearGradient id="jrSpk" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity=".2"/><stop offset="100%" stopColor={color} stopOpacity="0"/></linearGradient></defs>
                    <path d={`${pathD} L${W},${H} L0,${H} Z`} fill="url(#jrSpk)"/>
                    <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                );
              })()}
            </div>
          </div>

          {/* 2. Profit factor */}
          <div className={`zl-kpi zl-kpi--${parseFloat(s.pf)>=1.5?'green':parseFloat(s.pf)>=1?'gold':'red'}`}>
            <div className="zl-kpi-label">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="20" x2="4" y2="12"/><line x1="12" y1="20" x2="12" y2="6"/><line x1="20" y1="20" x2="20" y2="14"/></svg>
              Profit factor
            </div>
            <div className="zl-kpi-body">
              <div className={`zl-kpi-num zl-kpi-num--${parseFloat(s.pf)>=1.5?'green':parseFloat(s.pf)>=1?'gold':'red'}`}>{s.pf}</div>
              <svg width="52" height="52" viewBox="0 0 52 52" className="zl-kpi-ring">
                <circle cx="26" cy="26" r="20" fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="5"/>
                <circle cx="26" cy="26" r="20" fill="none"
                  stroke={parseFloat(s.pf)>=1.5?'var(--green)':parseFloat(s.pf)>=1?'var(--gold)':'var(--red)'}
                  strokeWidth="5"
                  strokeDasharray={`${2*Math.PI*20}`}
                  strokeDashoffset={`${2*Math.PI*20*(1-Math.min(parseFloat(s.pf)||0,3)/3)}`}
                  strokeLinecap="round"
                  transform="rotate(-90 26 26)"/>
              </svg>
            </div>
          </div>

          {/* 3. Trade win % */}
          <div className={`zl-kpi zl-kpi--${s.wr >= 50 ? 'green' : 'red'}`}>
            <div className="zl-kpi-label">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
              Trade win %
            </div>
            <div className="zl-kpi-body">
              <div className={`zl-kpi-num zl-kpi-num--${s.wr >= 50 ? 'green' : 'red'}`}>{s.wr}%</div>
              <div className="zl-kpi-donut">
                <svg viewBox="0 0 44 44">
                  <circle cx="22" cy="22" r="17" fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="4.5"/>
                  <circle cx="22" cy="22" r="17" fill="none"
                    stroke={s.wr >= 50 ? 'var(--green)' : 'var(--red)'}
                    strokeWidth="4.5"
                    strokeDasharray={`${2*Math.PI*17}`}
                    strokeDashoffset={`${2*Math.PI*17*(1-(s.total?(s.wins/s.total)*100:0)/100)}`}
                    strokeLinecap="round"
                    transform="rotate(-90 22 22)"/>
                </svg>
                <div className="zl-kpi-donut-label">
                  <span className="zl-kpi-donut-main">{s.wins}</span>
                  <span className="zl-kpi-donut-sub">{s.losses}</span>
                </div>
              </div>
            </div>
            <div style={{fontSize:'9px',color:'var(--gray)',marginTop:2}}>
              <span style={{color:'var(--green)'}}>{s.wins}W</span>
              {' · '}
              <span style={{color:'var(--red)'}}>{s.losses}L</span>
              {s.total - s.wins - s.losses > 0 && <span> · {s.total - s.wins - s.losses} BE</span>}
            </div>
          </div>

          {/* 4. Avg win/loss trade */}
          <div className="zl-kpi zl-kpi--blue">
            <div className="zl-kpi-label">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              Avg win/loss trade
            </div>
            <div className="zl-kpi-body">
              <div className="zl-kpi-num zl-kpi-num--white">{s.avgrr}R</div>
              <div className="zl-kpi-bar-wrap">
                <div className="zl-kpi-bar-row">
                  <div className="zl-kpi-bar-track">
                    <div className="zl-kpi-bar-fill--green" style={{width:`${Math.min(100,(s.aw/(s.aw+Math.abs(s.al)||1))*100)}%`}}/>
                  </div>
                  <span className="zl-kpi-bar-val" style={{color:'var(--green)'}}>{fmt$(s.aw)}</span>
                </div>
                <div className="zl-kpi-bar-row">
                  <div className="zl-kpi-bar-track">
                    <div className="zl-kpi-bar-fill--red" style={{width:`${Math.min(100,(Math.abs(s.al)/(s.aw+Math.abs(s.al)||1))*100)}%`}}/>
                  </div>
                  <span className="zl-kpi-bar-val" style={{color:'var(--red)'}}>-{fmt$(Math.abs(s.al))}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── LAYOUT ── */}
      <div className="journal-layout">
        <div className="journal-table-col">

          {/* Search */}
          <div className="search-wrap">
            <svg className="search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search trades — pair, strategy, notes…"
              aria-label="Search trades" className="txt-input search-input"/>
            {search && <button onClick={()=>setSearch('')} className="txt-body search-clear">×</button>}
          </div>

          {/* Filters */}
          <div className="filter-bar mb-2">
            <div className="filter-bar-top">
              <div className="filter-label" onClick={()=>setFiltersOpen(v=>!v)}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                FILTER
                {hasActive && <span className="filter-active-dot"/>}
                <svg className={`filter-caret ${filtersOpen?'open':''}`} width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              </div>

              {filtersOpen && (
                <div className="filter-selects-row">
                  <select className="filter-select" value={filterPair} onChange={e=>setFilterPair(e.target.value)}>
                    <option value="">All Pairs</option>
                    {PAIR_GROUPS.map(g=><optgroup key={g.label} label={g.label}>{g.pairs.map(p=><option key={p} value={p}>{p}</option>)}</optgroup>)}
                  </select>
                  <select className="filter-select" value={filterDir} onChange={e=>setFilterDir(e.target.value)}>
                    <option value="">Direction</option>
                    <option value="BUY">BUY</option>
                    <option value="SELL">SELL</option>
                  </select>
                  <select className="filter-select" value={filterRes} onChange={e=>setFilterRes(e.target.value)}>
                    <option value="">All Results</option>
                    <option value="win">Wins only</option>
                    <option value="loss">Losses only</option>
                  </select>
                  {/* Strategy dropdown */}
                  <div ref={stratRef} className="strat-dd-wrap">
                    <button onClick={()=>setStratOpen(v=>!v)}
                      className={`txt-caption strat-dd-btn ${filterStrat?'has-value':''}`}>
                      <span className="strat-dd-btn-text">{filterStrat || 'Strategy'}</span>
                      <svg className={`strat-dd-caret ${stratOpen?'open':''}`} width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {stratOpen && (
                      <div className="strat-dd-list">
                        <div onClick={()=>{setFilterStrat('');setStratOpen(false);}}
                          className={`txt-caption strat-dd-item ${!filterStrat?'active':''}`}>
                          All Strategies
                        </div>
                        {STRATEGIES.map(s=>(
                          <div key={s} onClick={()=>{setFilterStrat(s);setStratOpen(false);}}
                            className={`txt-caption strat-dd-item ${filterStrat===s?'active':''}`}>
                            <span>{s}</span>
                            {filterStrat===s && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="filter-actions">
                <span className="txt-micro filter-count">{filtered.length} trade{filtered.length!==1?'s':''}</span>
                {hasActive && <button className="btn-outline txt-micro pad-2-8" onClick={clearFilters}>Clear</button>}
              </div>
            </div>

            {/* Mobile filter grid */}
            {filtersOpen && (
              <div className="filter-grid">
                <select className="filter-select" value={filterPair} onChange={e=>setFilterPair(e.target.value)}>
                  <option value="">All Pairs</option>
                  {PAIR_GROUPS.map(g=><optgroup key={g.label} label={g.label}>{g.pairs.map(p=><option key={p} value={p}>{p}</option>)}</optgroup>)}
                </select>
                <select className="filter-select" value={filterDir} onChange={e=>setFilterDir(e.target.value)}>
                  <option value="">Direction</option>
                  <option value="BUY">BUY</option>
                  <option value="SELL">SELL</option>
                </select>
                <select className="filter-select" value={filterRes} onChange={e=>setFilterRes(e.target.value)}>
                  <option value="">All Results</option>
                  <option value="win">Wins</option>
                  <option value="loss">Losses</option>
                </select>
                <select className="filter-select" value={filterStrat} onChange={e=>setFilterStrat(e.target.value)}>
                  <option value="">Strategy</option>
                  {STRATEGIES.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* ── Desktop Table ── */}
          <div className="tbl-desktop">
            <table>
              <thead>
                <tr className="tbl-head-row">
                  <th className="tbl-th tbl-th-checkbox">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Select all trades" className="tbl-checkbox"/>
                  </th>
                  <th className="tbl-th tbl-th-bar"/>
                  {[['date','DATE'],['time','TIME'],['pair','INSTRUMENT'],['','DIR'],['','ENTRY'],['','S/L'],['','T/P'],['result','P & L ($)'],['','PIPS'],['','R:R'],['','STRATEGY'],['','CHART'],['','']].map(([k,l],i)=>(
                    <th key={i} onClick={k?()=>srt(k):undefined}
                      className={`txt-th tbl-th ${k?'sortable':''}`}>
                      {l}<Arrow k={k}/>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? Array.from({ length: 8 }, (_, i) => <SkeletonTableRow key={i} />)
                  : paginated.map((t,i)=>(
                  <tr key={t.id} className={`tbl-tr${selected.has(t.id)?' selected':''}`}
                    style={{animation:'rFade .2s ease both',animationDelay:`${Math.min(i,12)*0.03}s`}}>
                    <td className="tbl-td-check">
                      <input type="checkbox" checked={selected.has(t.id)} onChange={()=>toggleOne(t.id)} aria-label={`Select trade ${t.pair} ${t.date}`} className="tbl-checkbox"/>
                    </td>
                    <td style={{padding:'10px 0'}}>
                      <div className={`tbl-result-bar ${t.result>0?'pos':'neg'}`}/>
                    </td>
                    <td className="tbl-td tbl-td-pad">
                      <div className="txt-caption fw-semibold tbl-date-main">{t.date?.slice(5)?.replace('-',' ')}</div>
                      <div className="txt-caption tbl-date-year">{t.date?.slice(0,4)}</div>
                    </td>
                    <td className="tbl-td tbl-td-pad">
                      {t.time
                        ? <div className="tbl-time-wrap"><svg className="tbl-time-icon" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg><span className="txt-caption fw-semibold tbl-time-text">{t.time}</span></div>
                        : <span className="txt-caption tbl-time-empty">—</span>}
                    </td>
                    <td className="tbl-td tbl-td-pad">
                      <div className="txt-body-sm fw-semibold">{t.pair}</div>
                      <div className="txt-caption txt-muted tbl-session">{t.session}</div>
                    </td>
                    <td className="tbl-td tbl-td-pad"><span className={`dir-badge ${t.dir==='BUY'?'buy':'sell'}`}>{t.dir}</span></td>
                    <td className="txt-caption tbl-td tbl-td-pad tbl-entry-val">{t.entry}</td>
                    <td className="txt-caption txt-danger tbl-td tbl-td-pad opacity-75">{t.sl}</td>
                    <td className="txt-caption txt-success tbl-td tbl-td-pad opacity-75">{t.tp}</td>
                    <td className="tbl-td tbl-td-pad">
                      <span className={`txt-body-sm fw-semibold ${t.result>0?'tbl-pnl-pos':'tbl-pnl-neg'}`}>{fmt$(t.result)}</span>
                      <div className="txt-micro tbl-lot">{t.lotSize}L</div>
                    </td>
                    <td className={`txt-caption fw-semibold tbl-td tbl-td-pad ${calcPips(t)>0?'tbl-pips-pos':calcPips(t)<0?'tbl-pips-neg':'tbl-pips-flat'}`}>
                      {`${calcPips(t)>0?'+':''}${calcPips(t).toFixed(0)}`}
                    </td>
                    <td className="txt-caption fw-semibold tbl-rr tbl-td tbl-td-pad">{calcRR(t) != null ? `${calcRR(t)}R` : '—'}</td>
                    <td className="txt-caption txt-truncate tbl-td tbl-td-pad max-w-130">
                      {t.strat
                        ? <span className="txt-caption txt-truncate tbl-strat-chip">{t.strat}</span>
                        : <span className="tbl-strat-empty">—</span>}
                    </td>
                    <td className="tbl-td tbl-td-pad">
                      <div className={`tbl-thumb ${t.img?'':'tbl-thumb-empty'}`}>
                        {t.img ? <img src={t.img} alt=""/> : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}
                      </div>
                    </td>
                    <td className="tbl-td tbl-td-pad">
                      <div className="tbl-actions">
                        <button className="action-btn action-btn-view" onClick={()=>setViewTrade(t)} aria-label="View trade" title="View"><IcoView/></button>
                        <button className="action-btn action-btn-edit" onClick={()=>onEdit(t)}        aria-label="Edit trade"  title="Edit"><IcoEdit/></button>
                        <button className="action-btn action-btn-del"  onClick={()=>deleteSingle(t.id)} aria-label="Delete trade" title="Delete"><IcoDel/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!isLoading && !filtered.length && (
                  <tr><td colSpan={15} className="tbl-empty-cell">
                    {trades.length === 0 ? (
                      <div className="tbl-empty-inner">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--empty-svg-stroke)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                        <div className="txt-body-sm fw-semibold tbl-empty-text">No trades logged yet</div>
                        <div className="txt-caption tbl-empty-sub">Every trade logged is a lesson captured.</div>
                        <button className="btn-gold pad-7-18" onClick={onNewTrade}>+ Log Your First Trade</button>
                      </div>
                    ) : (
                      <div className="txt-caption tbl-no-results">
                        No trades match current filters.{' '}
                        {(filterPair||filterDir||filterRes||filterStrat||search) && (
                          <button onClick={clearFilters} className="txt-caption tbl-clear-btn">Clear filters</button>
                        )}
                      </div>
                    )}
                  </td></tr>
                )}
              </tbody>
            </table>

            <div className="tbl-load-more">
              <span className="txt-micro lm-showing">
                Showing <b className="txt-micro lm-count">{Math.min(visibleCount,filtered.length)}</b> of <b className="txt-micro lm-count">{filtered.length}</b> trades
              </span>
              {hasMore && (
                <button onClick={()=>setVisibleCount(v=>v+LOAD_MORE_STEP)}
                  className="load-more-btn txt-btn">
                  Load {Math.min(LOAD_MORE_STEP, filtered.length - visibleCount)} more ↓
                </button>
              )}
              {!hasMore && filtered.length > LOAD_MORE_STEP && (
                <span className="txt-micro lm-all">All {filtered.length} trades loaded</span>
              )}
            </div>
          </div>

          {/* ── Mobile Card List ── */}
          <div className="trade-card trade-card-wrap">
            {!isLoading && !filtered.length && (
              <div className="flex-center-pad32">
                {trades.length === 0 ? (
                  <div className="tbl-empty-inner">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--empty-svg-stroke)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                    <div className="txt-body-sm fw-semibold tbl-empty-text">No trades logged yet</div>
                    <div className="txt-caption tbl-empty-sub">Every trade logged is a lesson captured.</div>
                    <button className="btn-gold pad-7-18" onClick={onNewTrade}>+ Log Your First Trade</button>
                  </div>
                ) : (
                  <div className="txt-caption tbl-no-results">No trades match filters</div>
                )}
              </div>
            )}
            {isLoading && Array.from({length:5},(_,i)=>(
              <div key={i} className="mc-row">
                <div className="mc-row1">
                  <div className="skeleton" style={{width:14,height:14,flexShrink:0}}/>
                  <div className="mc-skeleton-bar"/>
                  <div className="flex-1">
                    <div className="skeleton skeleton-text" style={{width:'60%',marginBottom:6}}/>
                    <div className="skeleton skeleton-text" style={{width:'40%'}}/>
                  </div>
                </div>
              </div>
            ))}
            {!isLoading && paginated.map(t=>(
              <div key={t.id} className={`mc-row ${selected.has(t.id)?'selected':''}`}>
                <div className="mc-row1">
                  <input type="checkbox" checked={selected.has(t.id)} onChange={()=>toggleOne(t.id)} aria-label={`Select trade ${t.pair} ${t.date}`} style={{accentColor:'var(--gold)',cursor:'pointer',width:14,height:14,flexShrink:0}}/>
                  <div className={`mc-bar ${t.result>0?'pos':'neg'}`}/>
                  <div className="mc-meta">
                    <div className="mc-row1-hd">
                      <span className="txt-body fw-bold">{t.pair}</span>
                      <span className={`dir-badge ${t.dir==='BUY'?'buy':'sell'}`}>{t.dir}</span>
                      {t.session && <span className="txt-caption mc-session-tag">{t.session}</span>}
                    </div>
                    <div className="txt-caption txt-muted">{t.strat||'—'} · {t.date}{t.time?' · '+t.time:''}</div>
                  </div>
                </div>

                <div className="mc-metrics card-metrics-row">
                  {[
                    ['P&L', fmt$(t.result), t.result>0?'var(--green)':'var(--red)'],
                    ['Pips', `${calcPips(t)>0?'+':''}${calcPips(t).toFixed(0)}`, calcPips(t)>0?'var(--green)':calcPips(t)<0?'var(--red)':'var(--gray)'],
                    ['R:R', calcRR(t) != null ? `${calcRR(t)}R` : '—', 'var(--gold)'],
                    ['Lot', `${t.lotSize}L`, 'rgba(240,234,214,.5)'],
                  ].map(([label,val,color])=>(
                    <div key={label} className="mc-metric-cell">
                      <div className="txt-micro mc-metric-label">{label}</div>
                      <div className="txt-body-sm fw-bold" style={{color}}>{val}</div>
                    </div>
                  ))}
                </div>

                <div className="mc-prices card-prices-row">
                  {[['Entry',t.entry,'mc-price-entry'],['S/L',t.sl,'mc-price-sl'],['T/P',t.tp,'mc-price-tp']].map(([label,val,cls])=>(
                    <div key={label} className="txt-micro">
                      <span className="mc-price-label">{label}:</span>
                      <span className={`fw-semibold ${cls}`}>{val||'—'}</span>
                    </div>
                  ))}
                </div>

                <div className="mc-row4">
                  <div className="mc-tags">
                    {t.emotion && <span className="txt-caption mc-emotion-tag">{t.emotion}</span>}
                    {t.strat   && <span className="txt-caption txt-truncate mc-strat-tag">{t.strat}</span>}
                  </div>
                  <div className="mc-actions">
                    <button className="action-btn action-btn-view-card" onClick={()=>setViewTrade(t)} aria-label="View trade" title="View full trade details"><IcoView/></button>
                    <button className="action-btn action-btn-edit-card" onClick={()=>onEdit(t)}        aria-label="Edit trade"  title="Edit trade"><IcoEdit/></button>
                    <button className="action-btn action-btn-del-card"  onClick={()=>deleteSingle(t.id)} aria-label="Delete trade" title="Delete trade"><IcoDel/></button>
                  </div>
                </div>
              </div>
            ))}

            {hasMore && (
              <div className="mc-load-more">
                <button onClick={()=>setVisibleCount(v=>v+LOAD_MORE_STEP)} className="txt-btn mc-load-btn">
                  Load More ({filtered.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT SIDEBAR ── */}
        <div className="journal-sidebar">
          <div className="widget">
            <div className="widget-label">WIN / LOSS RATIO</div>
            <div className="wl-chart-wrap">
              <canvas ref={pieRef} width="120" height="120" className="flex-shrink0"/>
              <div className="wl-legend">
                {[['#3DBE73','Wins',s.wins],['#D94F4F','Losses',s.losses]].map(([c,lbl,n])=>(
                  <div key={lbl} className="wl-legend-row">
                    <div className="wl-dot" style={{background:c,boxShadow:`0 0 4px ${c}`}}/>
                    <div>
                      <div className="txt-caption fw-semibold wl-legend-label">{lbl}</div>
                      <div className="txt-micro wl-legend-sub">{n} trades · {s.total?Math.round(n/s.total*100):0}%</div>
                    </div>
                  </div>
                ))}
                <div className="wl-total">
                  <div className="txt-micro wl-total-label">Total</div>
                  <div className="num-sm txt-accent">{s.total}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="widget">
            <div className="widget-label-row">
              <div className="widget-label">EQUITY CURVE</div>
              <div className="eq-legend">
                {[['var(--green)','Profit'],['var(--red)','Loss']].map(([c,l])=>(
                  <div key={l} className="eq-legend-item">
                    <div className="eq-legend-line" style={{background:c}}/>
                    <span className="txt-micro eq-legend-label">{l}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="canvas-100-hidden">
              <canvas ref={eqRef} className="canvas-block"/>
            </div>
          </div>

          <div className="widget">
            <div className="widget-label">STRATEGY BREAKDOWN</div>
            <StrategyBreakdown filtered={filtered} />
          </div>
        </div>
      </div>

      {/* ── VIEW TRADE MODAL ── */}
      {viewTrade && (
        <div className="vtm-overlay" onClick={() => setViewTrade(null)}>
          <div className="vtm-box" onClick={e => e.stopPropagation()}>
            <div className={`vtm-accent-bar ${viewTrade.result > 0 ? 'pos' : 'neg'}`}/>

            <div className="vtm-header">
              <div className="vtm-hd-left">
                <div className={`vtm-dir-bar ${viewTrade.result > 0 ? 'pos' : 'neg'}`}/>
                <div>
                  <div className="vtm-title-row">
                    <span className="vtm-pair">{viewTrade.pair}</span>
                    <span className={`vtm-dir-chip ${viewTrade.dir==='BUY'?'buy':'sell'}`}>{viewTrade.dir}</span>
                    {viewTrade.setupGrade && (
                      <span className={`vtm-grade-chip vtm-grade-${viewTrade.setupGrade.replace('+','p')}`}>
                        {viewTrade.setupGrade}
                      </span>
                    )}
                    {viewTrade.followedPlan === true  && <span className="vtm-plan-ok"><IcoCheck/> Followed Plan</span>}
                    {viewTrade.followedPlan === false && <span className="vtm-plan-broke"><IcoCross/> Broke Plan</span>}
                  </div>
                  <div className="vtm-meta">
                    <span>{viewTrade.date}</span>
                    {viewTrade.time    && <span>· {viewTrade.time}</span>}
                    {viewTrade.session && <span>· {viewTrade.session}</span>}
                  </div>
                </div>
              </div>

              <div className="vtm-hd-right">
                <div className={`vtm-pnl ${viewTrade.result > 0 ? 'pos' : 'neg'}`}>{fmt$(viewTrade.result)}</div>
                <div className="vtm-rr">{calcRR(viewTrade) != null ? `${calcRR(viewTrade)}R` : '—'}</div>
                <div className="vtm-pips">
                  {typeof calcPips(viewTrade) === 'number' ? `${calcPips(viewTrade) > 0 ? '+' : ''}${calcPips(viewTrade).toFixed(0)} pips` : ''}
                </div>
              </div>
              <button onClick={() => setViewTrade(null)} className="vtm-close-btn">×</button>
            </div>

            <div className="vtm-body">
              <div className="vtm-section-hd">Price &amp; Risk</div>
              <div className="vtm-grid5">
                {[
                  ['Entry',     viewTrade.entry,   null],
                  ['Stop Loss', viewTrade.sl,       'c-red'],
                  ['Take Profit',viewTrade.tp,      'c-green'],
                  ['Lot Size',  viewTrade.lotSize,  null],
                  ['Pips', `${calcPips(viewTrade)>0?'+':''}${calcPips(viewTrade).toFixed(0)}`, calcPips(viewTrade)>0?'c-green':'c-red'],
                ].map(([l, v, c]) => (
                  <div key={l} className="vtm-cell">
                    <div className="vtm-cell-label">{l}</div>
                    <div className={`vtm-cell-val ${c||''}`}>{v || '—'}</div>
                  </div>
                ))}
              </div>

              {viewTrade.notes && (
                <div className="vtm-note-block">
                  <div className="vtm-note-hd">Pre-Trade Notes</div>
                  <div className="vtm-note-text">{viewTrade.notes}</div>
                </div>
              )}
              {viewTrade.review && (
                <div className="vtm-note-block">
                  <div className="vtm-note-hd">Post-Trade Review</div>
                  <div className="vtm-note-text">{viewTrade.review}</div>
                </div>
              )}

              {(viewTrade.imgBefore || viewTrade.img || viewTrade.imgAfter) && (
                <TradeImageViewer
                  imgBefore={viewTrade.imgBefore || viewTrade.img}
                  imgAfter={viewTrade.imgAfter}
                />
              )}
            </div>

            <div className="vtm-footer">
              <button onClick={() => { onEdit(viewTrade); setViewTrade(null); }} className="vtm-btn-edit">✏ Edit Trade</button>
              <button onClick={() => { deleteSingle(viewTrade.id); setViewTrade(null); }} className="vtm-btn-del">🗑 Delete</button>
              <button onClick={() => setViewTrade(null)} className="vtm-btn-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}